import { forwardRef, useId, useRef, useState } from "react";
import type { KeyboardEvent, SelectHTMLAttributes } from "react";
import { Popover } from "radix-ui";
import { Check, ChevronDown, Search } from "lucide-react";

import { cn } from "@/lib/cn";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useField } from "@/components/ui/Field";
import { FIELD_BASE, FIELD_INVALID } from "@/components/ui/Input";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: readonly SelectOption[];
  /** Shown as a non-selectable first row while the value is empty. */
  placeholder?: string;
  invalid?: boolean;
  /** 32px rather than 36: for a toolbar or a card heading, not a form. */
  dense?: boolean;
}

/** More options than this and the menu gets a box to type in. */
const SEARCH_FROM = 8;

/**
 * A select.
 *
 * Two things under one name, and which one is drawn is not the caller's
 * business.
 *
 * On a phone, and for any select a form has registered, it is the browser's
 * own. Employees fill this form on their phones; the platform picker is
 * faster, more accessible and more familiar than anything we would build,
 * and a registered select is written to by the form library behind React's
 * back, which only the real element survives.
 *
 * On a desktop, a select whose value the screen holds — a filter, a branch
 * chooser, the person a letter is about — opens a menu in the house style
 * instead: the same panel as every other menu, a tick on
 * the chosen row, and a box to type in once the list is long enough to need
 * one. The real select is still there underneath, unseen: choosing a row
 * sets it and fires its change event, so the caller's `onChange` gets the
 * same event from the same element either way.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, invalid, dense = false, id, value, onChange, ...props },
  ref,
) {
  const field = useField();
  const isInvalid = invalid ?? field?.invalid ?? false;
  // Registered selects are uncontrolled, so the placeholder styling is tracked
  // here rather than read from a `value` prop that is not passed.
  const [isEmpty, setIsEmpty] = useState(value === undefined || value === "");
  const desktop = useMediaQuery("(min-width: 1024px) and (pointer: fine)");
  const menu = desktop && typeof value === "string" && !props.multiple;
  const native = useRef<HTMLSelectElement | null>(null);

  const select = (
    <select
      ref={(node) => {
        native.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      id={menu ? undefined : (id ?? field?.inputId)}
      value={value}
      onChange={(event) => {
        setIsEmpty(event.target.value === "");
        onChange?.(event);
      }}
      aria-invalid={isInvalid || undefined}
      aria-describedby={field?.describedBy}
      {...props}
      // Under the menu it is only the element the change event comes from.
      tabIndex={menu ? -1 : props.tabIndex}
      aria-hidden={menu || undefined}
      className={
        menu
          ? "sr-only"
          : cn(
              FIELD_BASE,
              dense ? "h-8 appearance-none pr-8" : "h-9 appearance-none pr-9",
              isEmpty && placeholder && "text-fg-placeholder",
              isInvalid && FIELD_INVALID,
              className,
            )
      }
    >
      {placeholder ? (
        // Not disabled: a browser skips disabled options when choosing the
        // initial selection, which would silently select the first real
        // option instead of showing the placeholder. Validation rejects "".
        <option value="">{placeholder}</option>
      ) : null}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  if (!menu) {
    return (
      <div className="relative">
        {select}
        <ChevronDown
          className={cn(
            "pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-fg-subtle",
            dense ? "right-2.5" : "right-3",
          )}
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {select}
      <SelectMenu
        id={id ?? field?.inputId}
        label={props["aria-label"]}
        describedBy={field?.describedBy}
        options={options}
        placeholder={placeholder}
        value={value}
        invalid={isInvalid}
        dense={dense}
        disabled={props.disabled}
        className={className}
        onChoose={(next) => {
          const element = native.current;
          if (!element || element.value === next) return;
          element.value = next;
          element.dispatchEvent(new Event("change", { bubbles: true }));
        }}
      />
    </div>
  );
});

function SelectMenu({
  id,
  label,
  describedBy,
  options,
  placeholder,
  value,
  invalid,
  dense,
  disabled,
  className,
  onChoose,
}: {
  id?: string;
  label?: string;
  describedBy?: string;
  options: readonly SelectOption[];
  placeholder?: string;
  value: string;
  invalid: boolean;
  dense: boolean;
  disabled?: boolean;
  className?: string;
  onChoose: (value: string) => void;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  // The placeholder is a row too: it is how a filter is cleared.
  const rows: SelectOption[] = placeholder
    ? [{ value: "", label: placeholder }, ...options]
    : [...options];
  const searchable = options.length > SEARCH_FROM;
  const term = query.trim().toLowerCase();
  const shown = term ? rows.filter((row) => row.label.toLowerCase().includes(term)) : rows;
  const activeIndex = Math.min(active, Math.max(0, shown.length - 1));
  const chosen = options.find((option) => option.value === value);

  function toggle(next: boolean) {
    setOpen(next);
    if (next) {
      setQuery("");
      setActive(Math.max(0, rows.findIndex((row) => row.value === value)));
    }
  }

  function choose(row: SelectOption) {
    onChoose(row.value);
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(Math.min(shown.length - 1, activeIndex + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(0, activeIndex - 1));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActive(shown.length - 1);
    } else if (event.key === "Enter") {
      const row = shown[activeIndex];
      if (row) {
        event.preventDefault();
        choose(row);
      }
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={toggle}>
      <Popover.Trigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={label}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          className={cn(
            FIELD_BASE,
            "flex items-center justify-between gap-2 text-left",
            dense ? "h-8 pr-2.5" : "h-9",
            !chosen && "text-fg-placeholder",
            open && "ring-2 ring-focus",
            invalid && FIELD_INVALID,
            className,
          )}
        >
          <span className="truncate">{chosen?.label ?? placeholder ?? ""}</span>
          <ChevronDown className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onKeyDown={onKeyDown}
          className="z-50 flex max-h-[min(20rem,var(--radix-popover-content-available-height))] min-w-[max(11rem,var(--radix-popover-trigger-width))] max-w-96 flex-col overflow-hidden rounded-md bg-surface-raised shadow-pop outline-none ring-1 ring-line"
        >
          {searchable ? (
            <div className="flex items-center gap-2 border-b border-line-faint px-2.5">
              <Search className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
              <input
                autoFocus
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                }}
                placeholder="Search"
                aria-label="Search the options"
                className="h-9 w-full bg-transparent text-base text-fg outline-none placeholder:text-fg-placeholder"
              />
            </div>
          ) : null}
          <ul
            id={listId}
            role="listbox"
            aria-label={label}
            // With nothing to type in, the list itself takes the focus the
            // popover hands out on opening, and the keys with it.
            tabIndex={searchable ? -1 : 0}
            className="min-h-0 flex-1 overflow-y-auto p-1 outline-none"
          >
            {shown.length === 0 ? (
              <li className="px-2.5 py-3 text-base text-fg-subtle">Nothing by that name.</li>
            ) : null}
            {shown.map((row, index) => {
              const selected = row.value === value;
              return (
                <li
                  key={row.value || "placeholder"}
                  role="option"
                  aria-selected={selected}
                  ref={(node) => {
                    if (node && index === activeIndex) node.scrollIntoView({ block: "nearest" });
                  }}
                  onPointerMove={() => setActive(index)}
                  onClick={() => choose(row)}
                  className={cn(
                    "flex cursor-pointer select-none items-center gap-2 rounded-xs px-2.5 py-1.5 text-base",
                    index === activeIndex && "bg-ink-100",
                    row.value === "" ? "text-fg-subtle" : "text-fg",
                    selected && "font-medium",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{row.label}</span>
                  {selected ? (
                    <Check className="size-4 shrink-0 text-accent" aria-hidden="true" />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
